import type { MapMove } from '../types';

import dayjs from 'dayjs';
import { css } from '@emotion/css';
import { Theme, useTheme, Typography, LinearProgress, Tooltip, IconButton } from '@mui/material';
import { BiTargetLock, BiExpand } from 'react-icons/bi';

import Table from '@components/TableComponents/Table';
import Column from '@components/TableComponents/Table';
import MoveIdBadge from '@components/Badges/MoveIdBadge';
import MoveTypeBadge from '@components/Badges/MoveTypeBadge';
import MoveStatusBadge from '@components/Badges/MoveStatusBadge';
import { getFormattedVehicleFromMove } from '@services/moveService';

// TYPES --------------------------------------------------------------------- //

interface MoveLegendTableProps {
  moves: MapMove[];
  moveColorMap: Record<number, string>;
  highlightedMoveId: number | null;
  onHighlightMove: (moveId: number | null) => void;
  loading: boolean;
  showGeofences?: boolean;
  onToggleGeofences?: () => void;
}

// HELPERS ------------------------------------------------------------------- //

const formatTime = (time?: string) => {
  if (!time || typeof time !== 'string') return '-';
  let formatted = dayjs(time).format('M/D/YYYY h:mm A z');
  formatted = formatted.replace(/\s/g, '\xa0');
  return formatted;
};

// COMPONENT ----------------------------------------------------------------- //

const MoveLegendTable = ({
  moves,
  moveColorMap,
  highlightedMoveId,
  onHighlightMove,
  loading,
  showGeofences = true,
  onToggleGeofences,
}: MoveLegendTableProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  if (loading) {
    return (
      <div className={cls.container}>
        <LinearProgress />
      </div>
    );
  }

  return (
    <div className={cls.container}>
      <div className={cls.headerRow}>
        <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
          Move Routes ({moves.length})
        </Typography>
        <div className={cls.headerActions}>
          {highlightedMoveId != null ? (
            <Tooltip title='View all moves' arrow>
              <IconButton size='small' onClick={() => onHighlightMove(null)} className={cls.headerBtn}>
                <BiExpand size={14} />
              </IconButton>
            </Tooltip>
          ) : null}
          {onToggleGeofences ? (
            <Tooltip title={showGeofences ? 'Hide geofences' : 'Show geofences'} arrow>
              <IconButton size='small' onClick={onToggleGeofences} className={cls.headerBtn}>
                <BiTargetLock size={14} style={{ opacity: showGeofences ? 1 : 0.4 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <div className={cls.tableWrapper}>
        <Table
          data={moves}
          tableAriaLabel='move-routes'
          stickyHeader
          size='small'
          usePagination
          hidePagination
          disableSort
          rowClassName={(row: any) => (highlightedMoveId === row?.id ? cls.trHighlighted : undefined)}
          onRowClick={(row: any) => {
            const moveId = row?.id;
            onHighlightMove(highlightedMoveId === moveId ? null : moveId);
          }}
        >
          <Column
            name='color'
            renderer={(move: any) => {
              const color = moveColorMap[move.id] || theme.palette.grey[400];
              return <div className={cls.colorDot} style={{ backgroundColor: color }} />;
            }}
            label=''
            disableSort
            style={{ width: 32, padding: '6px 4px 6px 12px' }}
          />
          <Column name='move_id' renderer={(move: any) => <MoveIdBadge move={move} />} label='Move ID' />
          <Column name='move_type' renderer={(move: any) => <MoveTypeBadge move={move} />} label='Type' />
          <Column name='status' renderer={(move: any) => <MoveStatusBadge move={move} />} label='Status' />
          <Column
            name='lane'
            renderer={(move: any) => {
              const pickup = move?.lane?.pickup?.name || move?.lane?.pickup?.address || '-';
              const delivery = move?.lane?.delivery?.name || move?.lane?.delivery?.address || '-';
              return (
                <div>
                  <Typography variant='body2'>{pickup}</Typography>
                  <Typography variant='body2'>to {delivery}</Typography>
                </div>
              );
            }}
            label='Lane'
            style={{ minWidth: 192 }}
          />
          <Column
            name='vehicle'
            value={(move: any) => getFormattedVehicleFromMove(move, true, false)}
            label='Vehicle'
            style={{ minWidth: 144 }}
          />
          <Column
            name='reference_num'
            renderer={(move: any) => move?.reference_num || '-'}
            label='Ref #'
          />
          <Column
            name='pickup_time'
            renderer={(move: any) => formatTime(move?.pickup_time)}
            label='Pickup Time'
            style={{ minWidth: 144 }}
          />
          <Column
            name='delivery_time'
            renderer={(move: any) => formatTime(move?.delivery_time)}
            label='Delivery Time'
            style={{ minWidth: 144 }}
          />
        </Table>
      </div>
    </div>
  );
};

// STYLES -------------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
  `,
  headerRow: css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 4px 4px 12px;
    border-bottom: 1px solid ${theme?.palette?.divider};
  `,
  headerActions: css`
    display: flex;
    align-items: center;
    gap: 2px;
  `,
  headerBtn: css`
    color: ${theme?.palette?.text?.secondary} !important;
    padding: 4px !important;
    &:hover {
      color: ${theme?.palette?.text?.primary} !important;
      background-color: ${theme?.palette?.action?.hover} !important;
    }
  `,
  tableWrapper: css`
    flex: 1;
    min-height: 0;
    overflow: auto;
  `,
  trHighlighted: css`
    background-color: ${theme?.palette?.primary?.main}10 !important;
  `,
  colorDot: css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
  `,
});

export default MoveLegendTable;
